import uuid from "react-uuid";
import { ChatMessage, ConversationResponseEnvelope } from "@/api/models";

export const STREAM_MESSAGE_ROLE = {
  selection: "selection",
  summary: "summary",
  assistant: "assistant",
  compaction: "compaction",
  tool: "tool",
  image: "image",
  warning: "warning",
  imageProgress: "image_progress",
} as const;

export const getDialogueMessages = (messages: ChatMessage[]) =>
  messages.filter((message) => message.role !== STREAM_MESSAGE_ROLE.compaction);

export const preserveCompactionEventsOnError = (
  baseMessages: ChatMessage[],
  finalizedMessages: ChatMessage[],
) => {
  const finalizedEvents = new Map(
    finalizedMessages
      .filter((message) => message.role === STREAM_MESSAGE_ROLE.compaction)
      .map((message) => [message.id, message]),
  );
  const baseEventIds = new Set(
    baseMessages
      .filter((message) => message.role === STREAM_MESSAGE_ROLE.compaction)
      .map((message) => message.id),
  );
  const retainedBase = baseMessages.map((message) => {
    const finalizedEvent = finalizedEvents.get(message.id);
    return message.role === STREAM_MESSAGE_ROLE.compaction && finalizedEvent
      ? { ...message, ...finalizedEvent }
      : message;
  });
  const newEvents = [...finalizedEvents.values()].filter((message) => !baseEventIds.has(message.id));

  return normalizeCompactionTimeline([...retainedBase, ...newEvents]);
};

export type StreamMessageAccumulator = {
  selectionMessage: ChatMessage | null;
  summaryMessage: ChatMessage | null;
  assistantMessage: ChatMessage | null;
  mediaMessage: ChatMessage | null;
  toolMessage: ChatMessage | null;
  toolMessages: ChatMessage[];
  warningMessage: ChatMessage | null;
  compactionMessages: ChatMessage[];
  summaryContent: string;
  summaryIndex: number;
  assistantContent: string;
};

type StreamAccumulatorCallbacks = {
  setIsSelecting?: (value: boolean) => void;
  setIsSummarizing?: (value: boolean) => void;
  setIsQuerying?: (value: boolean) => void;
  setIsImageGenerating?: (value: boolean) => void;
  setShowLoadingMessage?: (value: boolean) => void;
  setCompactionStatus?: (message: string | null) => void;
};

export const createStreamMessageAccumulator = (): StreamMessageAccumulator => ({
  selectionMessage: null,
  summaryMessage: null,
  assistantMessage: null,
  mediaMessage: null,
  toolMessage: null,
  toolMessages: [],
  warningMessage: null,
  compactionMessages: [],
  summaryContent: "",
  summaryIndex: 0,
  assistantContent: "",
});

export const normalizeResponseMessages = (response: ConversationResponseEnvelope | ChatMessage): ChatMessage[] => {
  const topLevelMessage = response as ChatMessage;
  if (topLevelMessage.role === STREAM_MESSAGE_ROLE.compaction) {
    return [{
      ...topLevelMessage,
      id: topLevelMessage.id || uuid(),
      date: topLevelMessage.date || topLevelMessage.createdAt || new Date().toISOString(),
      content: typeof topLevelMessage.content === "string" ? topLevelMessage.content : "",
    }];
  }

  const envelope = response as ConversationResponseEnvelope;
  let lastModel = "";

  return (envelope.choices?.[0]?.messages || []).map((message) => {
    const normalized = {
      ...message,
      id: message.id || envelope.id || uuid(),
      date: message.date || message.createdAt || new Date().toISOString(),
    };

    if (envelope.model) {
      normalized.model = envelope.model;
      lastModel = envelope.model;
    } else if (!normalized.model && lastModel) {
      normalized.model = lastModel;
    }

    if (envelope.summary_index !== undefined) {
      normalized.summary_index = envelope.summary_index;
    }
    if (envelope.selection_index !== undefined) {
      normalized.selection_index = envelope.selection_index;
    }

    return normalized;
  });
};

export const shouldHideLoadingMessageForChunk = (messages: ChatMessage[]) => {
  const nonTerminalRoles = new Set<string>([
    STREAM_MESSAGE_ROLE.selection,
    STREAM_MESSAGE_ROLE.summary,
    STREAM_MESSAGE_ROLE.tool,
    STREAM_MESSAGE_ROLE.compaction,
    STREAM_MESSAGE_ROLE.imageProgress,
    "status",
  ]);

  return messages.some((message) => {
    if (nonTerminalRoles.has(message.role)) return false;

    // An assistant chunk that carries no text paints nothing: `applyStreamMessage`
    // only materializes a bubble once there is content to read. Knowledge-backed
    // turns open with exactly that — a citations-only chunk the backend labels
    // `assistant` — so treating it as terminal hid the loader while the screen was
    // still empty, leaving a gap until the first real delta.
    if (message.role === STREAM_MESSAGE_ROLE.assistant) return !!message.content?.trim();

    return true;
  });
};

// A turn can now carry multiple tool messages (e.g. several code_interpreter
// calls, or web_search + code_interpreter). The assistant-with-context branch
// reuses a stable id across chunks to keep its React key from changing, which
// means the same message arrives here repeatedly -- replace the existing
// entry in place instead of appending another copy. A message with a new id
// still appends, preserving first-seen order.
const upsertToolMessage = (accumulator: StreamMessageAccumulator, toolMessage: ChatMessage) => {
  const existingIndex = accumulator.toolMessages.findIndex((message) => message.id === toolMessage.id);
  if (existingIndex === -1) {
    accumulator.toolMessages.push(toolMessage);
  } else {
    accumulator.toolMessages[existingIndex] = toolMessage;
  }
};

export const applyStreamMessage = (
  accumulator: StreamMessageAccumulator,
  resultMessage: ChatMessage,
  callbacks: StreamAccumulatorCallbacks = {},
) => {
  if (resultMessage.role !== "status") callbacks.setCompactionStatus?.(null);

  switch (resultMessage.role) {
    case "status":
      if (resultMessage.status?.kind === "compacting") {
        callbacks.setCompactionStatus?.(resultMessage.status.message);
      } else if (resultMessage.status?.kind === "clear") {
        callbacks.setCompactionStatus?.(null);
      }
      return;

    case STREAM_MESSAGE_ROLE.selection:
      callbacks.setIsSelecting?.(false);
      accumulator.selectionMessage = resultMessage;
      return;

    case STREAM_MESSAGE_ROLE.compaction: {
      const eventMessage = {
        ...resultMessage,
        date: resultMessage.date || resultMessage.createdAt || new Date().toISOString(),
      };
      const existingIndex = accumulator.compactionMessages.findIndex((message) => message.id === eventMessage.id);
      if (existingIndex === -1) {
        accumulator.compactionMessages.push(eventMessage);
      } else {
        accumulator.compactionMessages[existingIndex] = eventMessage;
      }
      return;
    }

    case STREAM_MESSAGE_ROLE.summary:
      callbacks.setIsSummarizing?.(true);
      if (
        resultMessage.summary_index !== undefined &&
        resultMessage.summary_index > accumulator.summaryIndex
      ) {
        accumulator.summaryContent += "\n\n";
      }
      accumulator.summaryContent += resultMessage.content || "";
      accumulator.summaryIndex = resultMessage.summary_index || 0;
      accumulator.summaryMessage = {
        ...resultMessage,
        // Keep the id of the delta that opened this message. It accumulates
        // content across chunks, and the backend only pins a stable id on the
        // anthropic path -- elsewhere each chunk can carry a fresh one. Since
        // the id is the React key, letting it change remounts the whole rendered
        // message on every chunk.
        id: accumulator.summaryMessage?.id || resultMessage.id,
        content: accumulator.summaryContent,
      };
      return;

    case STREAM_MESSAGE_ROLE.assistant:
      callbacks.setIsSelecting?.(false);
      callbacks.setIsSummarizing?.(false);
      callbacks.setIsQuerying?.(false);
      accumulator.assistantContent += resultMessage.content || "";
      // Only materialize a message once there is something to read. The backend
      // accumulator applies the same rule, so creating one for empty or
      // whitespace-only deltas painted a blank bubble it would never persist.
      if (accumulator.assistantContent.trim()) {
        accumulator.assistantMessage = {
          ...resultMessage,
          // See the note on summaryMessage: stable id, or the message remounts
          // on every chunk.
          id: accumulator.assistantMessage?.id || resultMessage.id,
          content: accumulator.assistantContent,
        };
      }
      if (resultMessage.context) {
        const toolMessage = {
          id: accumulator.toolMessage?.id || `${resultMessage.id}:tool`,
          role: STREAM_MESSAGE_ROLE.tool,
          content: typeof resultMessage.context === 'string'
            ? resultMessage.context
            : JSON.stringify(resultMessage.context),
          date: resultMessage.date,
          model: resultMessage.model,
        };
        accumulator.toolMessage = toolMessage;
        upsertToolMessage(accumulator, toolMessage);
      }
      return;

    case STREAM_MESSAGE_ROLE.tool:
      accumulator.toolMessage = resultMessage;
      upsertToolMessage(accumulator, resultMessage);
      return;

    case STREAM_MESSAGE_ROLE.warning:
      accumulator.warningMessage = resultMessage;
      return;

    case STREAM_MESSAGE_ROLE.imageProgress:
      callbacks.setIsImageGenerating?.(true);
      return;

    case STREAM_MESSAGE_ROLE.image:
      callbacks.setIsImageGenerating?.(false);
      callbacks.setShowLoadingMessage?.(false);
      accumulator.mediaMessage = resultMessage;
      return;

    default:
      return;
  }
};

export const buildStreamMessages = (
  baseMessages: ChatMessage[],
  accumulator: StreamMessageAccumulator,
) => {
  return [
    ...insertCompactionMessages(baseMessages, accumulator.compactionMessages),
    ...collectStreamMessages(accumulator),
  ];
};

const insertCompactionMessages = (baseMessages: ChatMessage[], compactionMessages: ChatMessage[]) => {
  const messages = [...baseMessages];

  for (const compactionMessage of compactionMessages) {
    if (messages.some((message) => message.id === compactionMessage.id)) continue;

    const anchorIndex = compactionMessage.anchorMessageId
      ? messages.findIndex((message) => message.id === compactionMessage.anchorMessageId)
      : -1;
    if (anchorIndex === -1) {
      messages.push(compactionMessage);
      continue;
    }

    let insertionIndex = anchorIndex + 1;
    while (
      messages[insertionIndex]?.role === STREAM_MESSAGE_ROLE.compaction &&
      messages[insertionIndex]?.anchorMessageId === compactionMessage.anchorMessageId
    ) {
      const eventDate = compactionMessage.createdAt || compactionMessage.date || "";
      const siblingDate = messages[insertionIndex]?.createdAt || messages[insertionIndex]?.date || "";
      if (eventDate && siblingDate && siblingDate > eventDate) break;
      insertionIndex += 1;
    }
    messages.splice(insertionIndex, 0, compactionMessage);
  }

  return messages;
};

export const normalizeCompactionTimeline = (
  messages: ChatMessage[],
  { dropOrphans = false }: { dropOrphans?: boolean } = {},
) => {
  const dialogueMessages = messages.filter((message) => message.role !== STREAM_MESSAGE_ROLE.compaction);
  const dialogueIds = new Set(dialogueMessages.map((message) => message.id));
  const compactionMessages = messages.filter((message) => message.role === STREAM_MESSAGE_ROLE.compaction)
    .filter((message) => !dropOrphans || (!!message.anchorMessageId && dialogueIds.has(message.anchorMessageId)));
  return insertCompactionMessages(dialogueMessages, compactionMessages);
};

export const collectStreamMessages = (accumulator: StreamMessageAccumulator) => {
  const messages: ChatMessage[] = [];

  if (accumulator.selectionMessage) messages.push(accumulator.selectionMessage);
  if (accumulator.summaryMessage) messages.push(accumulator.summaryMessage);
  messages.push(...accumulator.toolMessages);
  if (accumulator.assistantMessage) messages.push(accumulator.assistantMessage);
  if (accumulator.mediaMessage) messages.push(accumulator.mediaMessage);
  if (accumulator.warningMessage) messages.push(accumulator.warningMessage);

  return messages;
};

export const readNdjsonStream = async (
  stream: ReadableStream<Uint8Array>,
  onObject: (value: unknown) => Promise<void> | void,
  // Opt in for endpoints that always close with __stream_done__/__stream_error__.
  // Without it, a stream that dies mid-answer — gateway idle timeout, proxy
  // reset, worker recycle — is indistinguishable from a normal finish, so the
  // caller keeps a half-written turn on screen and reports nothing.
  { requireTerminalMarker = false }: { requireTerminalMarker?: boolean } = {},
) => {
  const isTerminalMarker = (value: unknown) =>
    !!value &&
    typeof value === "object" &&
    (!!(value as Record<string, unknown>).__stream_done__ ||
      !!(value as Record<string, unknown>).__stream_error__);

  const parseLine = (line: string): unknown | null => {
    try {
      return JSON.parse(line);
    } catch {
      // Azure/WAF sometimes injects HTML error pages mid-stream (e.g. 502
      // after a successful prefix). Skip those lines silently.
      if (/^\s*</.test(line)) return null;

      // Try to extract a JSON object substring (defence against
      // trailing garbage appended to an otherwise valid NDJSON line).
      const jsonMatch = line.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          return JSON.parse(jsonMatch[0]);
        } catch {
          // fall through
        }
      }
      return null;
    }
  };

  const reader = stream.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffered = "";
  let parsedAny = false;
  let terminalSeen = false;
  let aborted = false;

  try {
    for (;;) {
      let value: Uint8Array | undefined;
      let done: boolean;

      try {
        ({ done, value } = await reader.read());
      } catch (e) {
        // "Stop generating" is a deliberate end, not a failure — the caller owns
        // that path. Any other disconnect means the turn was cut short: stop
        // reading and let the terminal-marker check below surface it.
        if ((e as { name?: string })?.name === "AbortError") {
          aborted = true;
          break;
        }
        if (parsedAny) {
          console.warn("Stream read error after content received:", e);
          break;
        }
        throw e;
      }

      if (done) break;

      buffered += decoder.decode(value!, { stream: true });
      const lines = buffered.split("\n");
      buffered = lines.pop() || "";

      for (const line of lines) {
        if (line === "" || line === "{}") continue;
        const parsed = parseLine(line);
        if (parsed !== null) {
          parsedAny = true;
          if (isTerminalMarker(parsed)) terminalSeen = true;
          await onObject(parsed);
        }
      }
    }

    buffered += decoder.decode();
    const trailing = buffered.trim();
    if (trailing && trailing !== "{}") {
      const parsed = parseLine(trailing);
      if (parsed !== null) {
        parsedAny = true;
        if (isTerminalMarker(parsed)) terminalSeen = true;
        await onObject(parsed);
      }
    }
  } finally {
    reader.releaseLock();
  }

  if (aborted) return;

  if (!parsedAny) {
    throw new Error("Received a malformed streaming response from the server.");
  }

  if (requireTerminalMarker && !terminalSeen) {
    throw new Error(
      "The connection dropped before the response finished, so it may be incomplete. Please try again.",
    );
  }
};
